import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { get, post } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// The detail endpoint exposes the current proof; only an approved event binds it
// to the human decision. A current digest alone is not authorization to send.
export function approvedEmail(approval) {
  if (!approval || approval.status !== "approved" || approval.action_type !== "send_outbound_message"
      || !["sales_message", "outbound_message"].includes(approval.source_type)
      || approval.execution_status !== "not_started"
      || !approval.source_id || !approval.source || approval.source.id !== approval.source_id
      || approval.source.approval_id !== approval.id || approval.source.channel !== "email"
      || !Number.isInteger(approval.content_version) || approval.content_version < 1
      || typeof approval.content_digest !== "string" || !approval.content_digest
      || typeof approval.source.subject !== "string" || typeof approval.source.body !== "string") return false;
  const approved = [...(approval.events || [])].reverse().find((event) => event.event_type === "approved");
  return approved?.metadata?.content_version === approval.content_version
    && approved?.metadata?.content_digest === approval.content_digest;
}

export default function LiveEmailSend({ approval, onRefresh }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const eligible = approvedEmail(approval);
  const source = approval?.source;
  const needLead = eligible && !source.contact_id && !!source.lead_id;
  const lead = useQuery({
    queryKey: ["crm-lead-email", approval?.organization_id, source?.lead_id],
    queryFn: () => get(`/crm/leads/${source.lead_id}`),
    enabled: !!needLead, retry: false,
  });
  const contactId = eligible ? (source.contact_id || (needLead ? lead.data?.contact_id : null)) : null;
  const contact = useQuery({
    queryKey: ["crm-contact-email", approval?.organization_id, contactId],
    queryFn: () => get(`/crm/contacts/${contactId}`),
    enabled: !!contactId, retry: false,
  });
  const recipient = contact.data?.email;
  const canSend = eligible && !!contactId && contact.data?.id === contactId
    && typeof recipient === "string" && recipient.trim().length > 0;
  const [review, setReview] = useState(null);
  const [sending, setSending] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const inFlight = useRef(false);
  // Until the server state is revalidated, even an ambiguous network failure
  // must not leave a second send control in this mounted UI.
  const actionable = canSend && !attempted;

  const confirm = async () => {
    if (!review || !actionable || inFlight.current || review.id !== source.id
        || review.content_version !== approval.content_version || review.content_digest !== approval.content_digest
        || review.recipient !== recipient || review.subject !== source.subject || review.body !== source.body) return;
    inFlight.current = true;
    setAttempted(true);
    setSending(true);
    try {
      await post(`/sales/messages/${review.id}/send-live`, {
        content_version: review.content_version, content_digest: review.content_digest,
      });
      setReview(null);
      toast.success(t("approvals.liveEmail.sent"));
    } catch (error) {
      setReview(null); // A timed-out send may have been claimed. Never offer a retry here.
      toast.error(["external_actions_disabled", "approval_content_changed", "recipient_suppressed"].includes(error?.code)
        ? errorMessage(error) : t("approvals.liveEmail.failed"));
    } finally {
      try {
        await Promise.allSettled([onRefresh(), qc.invalidateQueries({ queryKey: ["approvals"] }),
          qc.invalidateQueries({ queryKey: ["crm-contact-email"] }),
          qc.invalidateQueries({ queryKey: ["crm-lead-email"] })]);
      } finally {
        inFlight.current = false;
        setSending(false);
      }
    }
  };

  return (
    <>
      {actionable && <Button type="button" size="sm" data-testid="approval-send-live"
        onClick={() => setReview({ id: source.id, recipient, subject: source.subject, body: source.body,
          content_version: approval.content_version, content_digest: approval.content_digest })}>
        {t("approvals.liveEmail.action")}
      </Button>}
      <Dialog open={!!review} onOpenChange={(open) => { if (!open && !inFlight.current) setReview(null); }}>
        <DialogContent data-testid="approval-send-live-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("approvals.liveEmail.title")}</DialogTitle>
            <DialogDescription>{t("approvals.liveEmail.warning")}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-amber-800">{t("approvals.liveEmail.noRetry")}</p>
          <dl className="space-y-3 text-sm">
            <div><dt className="font-medium">{t("approvals.liveEmail.recipient")}</dt><dd className="break-all" data-testid="approval-send-recipient">{review?.recipient}</dd></div>
            <div><dt className="font-medium">{t("approvals.liveEmail.subject")}</dt><dd className="whitespace-pre-wrap break-words" data-testid="approval-send-subject">{review?.subject}</dd></div>
            <div><dt className="font-medium">{t("approvals.liveEmail.body")}</dt><dd className="max-h-52 overflow-y-auto whitespace-pre-wrap break-words" data-testid="approval-send-body">{review?.body}</dd></div>
          </dl>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={sending} onClick={() => setReview(null)} data-testid="approval-send-cancel">{t("approvals.liveEmail.cancel")}</Button>
            <Button type="button" disabled={sending || !actionable} onClick={confirm} data-testid="approval-send-confirm">
              {sending ? t("approvals.liveEmail.sending") : t("approvals.liveEmail.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}